import { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import {
  useCartState,
  useCartDispatch,
  CartActionType,
} from 'public/context/CartContext';
import { useTranslations } from '../../../../admin/pages/Coupons/hooks/use-translations';
import useCouponSummary, { CouponSummary } from './useCouponSummary';
import useMarketType from '../../../../utils/useMarketType';

export interface CouponValidationResponse {
  id: number;
  farm_id: number;
  code: string;
  discounts: Record<number, number>;
  valid?: boolean;
  error: string;
  discount_type: string;
  discount_value: number;
  apply_to_whole_cart: boolean;
}

export function useCoupons() {
  const t = useTranslations();
  const dispatch = useCartDispatch();
  const { coupons, farmProducts } = useCartState();
  const { marketType } = useMarketType();
  const summary: CouponSummary = useCouponSummary(farmProducts, coupons);
  const [code, setCode] = useState<string>('');

  const { appendData: validate, isLoading: validating } = useQueryApiClient({
    request: {
      url: `api/v2/coupons/validate`,
      method: 'POST',
    },
    onSuccess: (resp: CouponValidationResponse) => {
      if (resp.valid) {
        dispatch({
          type: CartActionType.ADD_COUPON,
          coupon: {
            id: resp.id,
            farm_id: resp.farm_id,
            discount_type: resp.discount_type,
            discount_value: resp.discount_value,
            apply_to_whole_cart: resp.apply_to_whole_cart,
            code,
            productDiscounts: resp.discounts,
          },
        });
        setCode('');
      } else if (resp.error) {
        message.error(t(resp.error));
      } else {
        message.error(t('cart_summary.coupon.error.default'));
      }
    },
    onError: () => {
      message.error(t('cart_summary.coupon.error.network'));
    },
  });

  const applyCoupon = useCallback(() => {
    if (coupons.find((c) => c.code === code)) {
      message.error(t('cart_summary.coupon.error.code'));
      return;
    }
    const alreadyDiscountedProductId = coupons
      .map((coupon: any) => Object.keys(coupon?.productDiscounts))
      .flat();

    const alreadyAppliedCouponIds = coupons.map((coupon: any) => coupon.id);

    const productIds = farmProducts
      .flatMap((farm) => farm.products)
      .map((p) => p.cartProduct?.id)
      .filter((id): id is number => Boolean(id));

    validate({
      code,
      product_ids: productIds,
      already_discounted_products_id: alreadyDiscountedProductId,
      already_applied_coupon_ids: alreadyAppliedCouponIds,
      market_type: marketType,
    });
  }, [code, coupons, farmProducts, marketType, t, validate]);

  const removeCoupon = useCallback(
    (codeToRemove: string) => {
      dispatch({ type: CartActionType.REMOVE_COUPON, code: codeToRemove });
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      code,
      setCode,
      coupons,
      validating,
      applyCoupon,
      removeCoupon,
      ...summary,
    }),
    [code, coupons, validating, applyCoupon, removeCoupon, summary]
  );
}
